import { useEffect, useState, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaCheck } from "react-icons/fa";
import "../../../Style/waterloo-payroll/index.css";
import { formatDate } from "Helper/Converters";
import { exportFileToExcel } from "Utils/utils";

const HaltonHillsPerTicketReport = ({ show, handleClose, data, user }) => {
  const [mixed, setMixed] = useState({});
  const [clear, setClear] = useState({});
  const [oneStationMultipliers, setOneStationMultipliers] = useState({
    mkStandard: "",
    mkAdditional: "",
    clStandard: "",
    clAdditional: "",
  });

  const [oneStationClearMultipliers, setOneStationClearMultipliers] = useState({
    mkStandard: "",
    mkAdditional: "",
    clStandard: "",
    clAdditional: "",
  });

  const [showGrandTotal, setShowGrandTotal] = useState(false);
  const [savedMultipliers, setSavedMultipliers] = useState(null);

  const calculateSubtotal = (total, multiplier) => {
    if (total === undefined || total === null || total === "") {
      return 0;
    }
    const totalNum = parseFloat(total) || 0;
    const mult = parseFloat(multiplier) || 0;
    const result = totalNum * mult;
    return isNaN(result) ? 0 : result;
  };

  const formatCurrency = (value) => {
    if (value === undefined || value === null || isNaN(value)) {
      return "";
    }
    return `$${value.toFixed(0)}`;
  };

  const hasValue = (value) => {
    return value !== undefined && value !== null && value !== "";
  };

  const calculateGrandTotal = (section, multipliers) => {
    const stations = ["oneStation"];
    const totals = {
      mkStandard: 0,
      mkAdditional: 0,
      clStandard: 0,
      clAdditional: 0,
    };

    stations.forEach((station) => {
      const stationData = section?.[station];
      const stationMultipliers = multipliers[station];

      if (stationData?.totals && stationMultipliers) {
        totals.mkStandard += calculateSubtotal(
          stationData.totals.mkStandard,
          stationMultipliers.mkStandard
        );
        totals.mkAdditional += calculateSubtotal(
          stationData.totals.mkAdditional,
          stationMultipliers.mkAdditional
        );
        totals.clStandard += calculateSubtotal(
          stationData.totals.clStandard,
          stationMultipliers.clStandard
        );
        totals.clAdditional += calculateSubtotal(
          stationData.totals.clAdditional,
          stationMultipliers.clAdditional
        );
      }
    });

    return totals;
  };

  const validateGrandTotalGeneration = () => {
    const stations = ["oneStation"];
    const multiplierGroups = [
      {
        section: mixed,
        multipliers: {
          oneStation: oneStationMultipliers,
        },
      },
      {
        section: clear,
        multipliers: {
          oneStation: oneStationClearMultipliers,
        },
      },
    ];

    for (const { section, multipliers } of multiplierGroups) {
      for (const station of stations) {
        const stationData = section?.[station];
        const stationMultipliers = multipliers[station];

        if (stationData?.totals) {
          const fields = [
            "mkStandard",
            "mkAdditional",
            "clStandard",
            "clAdditional",
          ];
          for (const field of fields) {
            const totalValue = stationData.totals[field];
            // Only require multiplier if the total value is greater than 0
            if (
              totalValue !== undefined &&
              totalValue !== null &&
              totalValue !== ""
            ) {
              const numericValue = parseFloat(totalValue) || 0;
              if (numericValue > 0) {
                const multiplierValue = stationMultipliers?.[field] || "";
                if (!multiplierValue || multiplierValue === "") {
                  return false;
                }
              }
            }
          }
        }
      }
    }

    return true;
  };

  const getCurrentMultipliers = () => {
    return {
      oneStation: { ...oneStationMultipliers },
      oneStationClear: { ...oneStationClearMultipliers },
    };
  };

  const hasMultipliersChanged = () => {
    if (!savedMultipliers || !showGrandTotal) {
      return true;
    }

    const current = getCurrentMultipliers();
    const saved = savedMultipliers;

    const compareMultipliers = (currentMulti, savedMulti) => {
      return (
        currentMulti.mkStandard !== savedMulti.mkStandard ||
        currentMulti.mkAdditional !== savedMulti.mkAdditional ||
        currentMulti.clStandard !== savedMulti.clStandard ||
        currentMulti.clAdditional !== savedMulti.clAdditional
      );
    };

    return (
      compareMultipliers(current.oneStation, saved.oneStation) ||
      compareMultipliers(current.oneStationClear, saved.oneStationClear)
    );
  };

  const handleGenerateGrandTotal = () => {
    if (!validateGrandTotalGeneration()) {
      alert("Please fill all multiplier fields before generating grand total.");
      return;
    }
    setSavedMultipliers(getCurrentMultipliers());
    setShowGrandTotal(true);
  };

  const resetAllStates = () => {
    setOneStationMultipliers({
      mkStandard: "",
      mkAdditional: "",
      clStandard: "",
      clAdditional: "",
    });
    setOneStationClearMultipliers({
      mkStandard: "",
      mkAdditional: "",
      clStandard: "",
      clAdditional: "",
    });
    setShowGrandTotal(false);
    setSavedMultipliers(null);
  };

  const handleModalClose = () => {
    resetAllStates();
    handleClose();
  };

  useEffect(() => {
    setMixed(data?.haltonHills?.mixed);
    setClear(data?.haltonHills?.pureClear);
    setShowGrandTotal(false);
    setSavedMultipliers(null);
  }, [data]);

  const mixedGrandTotal = useMemo(
    () =>
      showGrandTotal
        ? calculateGrandTotal(mixed, {
          oneStation: oneStationMultipliers,
        })
        : null,
    [
      showGrandTotal,
      mixed,
      oneStationMultipliers,
    ]
  );

  const clearGrandTotal = useMemo(
    () =>
      showGrandTotal
        ? calculateGrandTotal(clear, {
          oneStation: oneStationClearMultipliers,
        })
        : null,
    [
      showGrandTotal,
      clear,
      oneStationClearMultipliers,
    ]
  );

  const formatMixedDataForExcel = () => {
    const formattedData = [
      [
        {
          v: "Station Code",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "MK Standard",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "MK Standard Additional",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "CL Standard",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "CL Standard Additional",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
      ],
    ];

    const stationTypes = [
      { key: "oneStation", label: "One Station Code's" },
    ];

    stationTypes.forEach(({ key, label }) => {
      const stationData = mixed?.[key];
      if (stationData?.rows && stationData.rows.length > 0) {
        formattedData.push(
          [],
          [{ v: label, t: "s", s: { font: { bold: true } } }]
        );

        stationData.rows.forEach((row) => {
          const stationCode =
            key === "oneStation"
              ? row?.station
              : row?.stationCodes?.join(" ") || "";
          formattedData.push([
            { v: stationCode },
            {
              v:
                key === "oneStation"
                  ? row?.mkStandard
                  : row?.totals?.mkStandard || 0,
            },
            {
              v:
                key === "oneStation"
                  ? row?.mkAdditional
                  : row?.totals?.mkAdditional || 0,
            },
            {
              v:
                key === "oneStation"
                  ? row?.clStandard
                  : row?.totals?.clStandard || 0,
            },
            {
              v:
                key === "oneStation"
                  ? row?.clAdditional
                  : row?.totals?.clAdditional || 0,
            },
          ]);
        });

        const multipliers = oneStationMultipliers;

        formattedData.push(
          [
            { v: "Total", t: "s", s: { font: { bold: true } } },
            {
              v: stationData.totals?.mkStandard || 0,
              s: { font: { bold: true } },
            },
            {
              v: stationData.totals?.mkAdditional || 0,
              s: { font: { bold: true } },
            },
            {
              v: stationData.totals?.clStandard || 0,
              s: { font: { bold: true } },
            },
            {
              v: stationData.totals?.clAdditional || 0,
              s: { font: { bold: true } },
            },
          ],
          [
            { v: "Multiplier" },
            { v: multipliers.mkStandard || "" },
            { v: multipliers.mkAdditional || "" },
            { v: multipliers.clStandard || "" },
            { v: multipliers.clAdditional || "" },
          ],
          [
            { v: "Sub Total", t: "s", s: { font: { bold: true } } },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.mkStandard,
                  multipliers.mkStandard
                )
              ),
              s: { font: { bold: true } },
            },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.mkAdditional,
                  multipliers.mkAdditional
                )
              ),
              s: { font: { bold: true } },
            },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.clStandard,
                  multipliers.clStandard
                )
              ),
              s: { font: { bold: true } },
            },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.clAdditional,
                  multipliers.clAdditional
                )
              ),
              s: { font: { bold: true } },
            },
          ]
        );
      }
    });

    if (mixedGrandTotal) {
      formattedData.push(
        [],
        [
          { v: "Mixed Grand Total", t: "s", s: { font: { bold: true } } },
          {
            v: formatCurrency(mixedGrandTotal.mkStandard),
            s: { font: { bold: true } },
          },
          {
            v: formatCurrency(mixedGrandTotal.mkAdditional),
            s: { font: { bold: true } },
          },
          {
            v: formatCurrency(mixedGrandTotal.clStandard),
            s: { font: { bold: true } },
          },
          {
            v: formatCurrency(mixedGrandTotal.clAdditional),
            s: { font: { bold: true } },
          },
        ]
      );
    }

    return formattedData;
  };

  const formatClearDataForExcel = () => {
    const formattedData = [
      [
        {
          v: "Station Code",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "MK Standard",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "MK Standard Additional",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "CL Standard",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
        {
          v: "CL Standard Additional",
          t: "s",
          s: { font: { bold: true, sz: 16, color: { rgb: "0068af" } } },
        },
      ],
    ];

    const stationTypes = [
      { key: "oneStation", label: "One Station Code's" },
    ];

    stationTypes.forEach(({ key, label }) => {
      const stationData = clear?.[key];
      if (stationData?.rows && stationData.rows.length > 0) {
        formattedData.push(
          [],
          [{ v: label, t: "s", s: { font: { bold: true } } }]
        );

        stationData.rows.forEach((row) => {
          const stationCode =
            key === "oneStation"
              ? row?.station
              : row?.stationCodes?.join(" ") || "";
          formattedData.push([
            { v: stationCode },
            {
              v:
                key === "oneStation"
                  ? row?.mkStandard
                  : row?.totals?.mkStandard || 0,
            },
            {
              v:
                key === "oneStation"
                  ? row?.mkAdditional
                  : row?.totals?.mkAdditional || 0,
            },
            {
              v:
                key === "oneStation"
                  ? row?.clStandard
                  : row?.totals?.clStandard || 0,
            },
            {
              v:
                key === "oneStation"
                  ? row?.clAdditional
                  : row?.totals?.clAdditional || 0,
            },
          ]);
        });

        const multipliers = oneStationClearMultipliers;

        formattedData.push(
          [
            { v: "Total", t: "s", s: { font: { bold: true } } },
            {
              v: stationData.totals?.mkStandard || 0,
              s: { font: { bold: true } },
            },
            {
              v: stationData.totals?.mkAdditional || 0,
              s: { font: { bold: true } },
            },
            {
              v: stationData.totals?.clStandard || 0,
              s: { font: { bold: true } },
            },
            {
              v: stationData.totals?.clAdditional || 0,
              s: { font: { bold: true } },
            },
          ],
          [
            { v: "Multiplier" },
            { v: multipliers.mkStandard || "" },
            { v: multipliers.mkAdditional || "" },
            { v: multipliers.clStandard || "" },
            { v: multipliers.clAdditional || "" },
          ],
          [
            { v: "Sub Total", t: "s", s: { font: { bold: true } } },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.mkStandard,
                  multipliers.mkStandard
                )
              ),
              s: { font: { bold: true } },
            },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.mkAdditional,
                  multipliers.mkAdditional
                )
              ),
              s: { font: { bold: true } },
            },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.clStandard,
                  multipliers.clStandard
                )
              ),
              s: { font: { bold: true } },
            },
            {
              v: formatCurrency(
                calculateSubtotal(
                  stationData.totals?.clAdditional,
                  multipliers.clAdditional
                )
              ),
              s: { font: { bold: true } },
            },
          ]
        );
      }
    });

    if (clearGrandTotal) {
      formattedData.push(
        [],
        [
          { v: "Clear Grand Total", t: "s", s: { font: { bold: true } } },
          {
            v: formatCurrency(clearGrandTotal.mkStandard),
            s: { font: { bold: true } },
          },
          {
            v: formatCurrency(clearGrandTotal.mkAdditional),
            s: { font: { bold: true } },
          },
          {
            v: formatCurrency(clearGrandTotal.clStandard),
            s: { font: { bold: true } },
          },
          {
            v: formatCurrency(clearGrandTotal.clAdditional),
            s: { font: { bold: true } },
          },
        ]
      );
    }

    return formattedData;
  };

  const handleExportToExcel = () => {
    if (!showGrandTotal) {
      alert(
        'Firstly, generate grand total by clicking on "Generate Grand Total" button'
      );
      return;
    }

    const mixedData = formatMixedDataForExcel();
    const clearData = formatClearDataForExcel();
    const combinedData = [...mixedData, ...clearData];

    const startDate = user?.startDate
      ? new Date(user.startDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      : "";
    const endDate = user?.endDate
      ? new Date(user.endDate).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      : "";

    const fileName = `${user?.name || "Per Tickets"} Report ${startDate}${endDate ? `-${endDate}` : ""
      }`;

    exportFileToExcel(combinedData, fileName);
  };

  return (
    <Modal show={show} onHide={handleModalClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          Per Ticket Report - {user?.name} {" ("}
          {formatDate(new Date(user?.endDate), "dd/MM/yyyy")} -{" "}
          {formatDate(new Date(user?.startDate), "dd/MM/yyyy")}
          {")"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ padding: "1rem", overflowX: "auto" }}>
        {!data ||
          (!mixed?.oneStation &&
            !clear?.oneStation) ? (
          <div className="text-center p-4">
            <p>No data available for the Per Ticket Report.</p>
            <p className="text-muted">
              Please ensure the report has been generated successfully.
            </p>
          </div>
        ) : (
          <div className="payroll-table-container" style={{ width: "100%" }}>
            {/* Mixed Section */}
            {mixed?.oneStation && (
              <>
                <div
                  style={{
                    marginTop: "1rem",
                    marginBottom: "1rem",
                    backgroundColor: "#0068af",
                    color: "white",
                    padding: "12px 16px",
                    borderRadius: "4px",
                    fontWeight: "600",
                    fontSize: "18px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  Mixed Units - MK + CL
                </div>
                {/* One Station Table */}
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th>One Station Code's</th>
                      <th>MK Standard</th>
                      <th>MK Standard Additional</th>
                      <th>CL Standard</th>
                      <th>CL Standard Additional</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* <tr>
                  <td className="section-header" colSpan={5}>
                    One Station Code
                  </td>
                </tr> */}
                    {mixed?.oneStation?.rows?.map((row, index) => (
                      <tr key={index}>
                        <td>{row?.station}</td>
                        <td>{row?.mkStandard}</td>
                        <td>{row?.mkAdditional}</td>
                        <td>{row?.clStandard}</td>
                        <td>{row?.clAdditional}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td style={{ fontWeight: "bold" }}>Total</td>
                      <td>{mixed?.oneStation?.totals?.mkStandard}</td>
                      <td>{mixed?.oneStation?.totals?.mkAdditional}</td>
                      <td>{mixed?.oneStation?.totals?.clStandard}</td>
                      <td>{mixed?.oneStation?.totals?.clAdditional}</td>
                    </tr>
                    <tr className="multiplier-row">
                      <td>Multiplier</td>
                      <td>
                        <input
                          type="number"
                          value={oneStationMultipliers.mkStandard}
                          onChange={(e) =>
                            setOneStationMultipliers({
                              ...oneStationMultipliers,
                              mkStandard: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(mixed?.oneStation?.totals?.mkStandard)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={oneStationMultipliers.mkAdditional}
                          onChange={(e) =>
                            setOneStationMultipliers({
                              ...oneStationMultipliers,
                              mkAdditional: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(mixed?.oneStation?.totals?.mkAdditional)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={oneStationMultipliers.clStandard}
                          onChange={(e) =>
                            setOneStationMultipliers({
                              ...oneStationMultipliers,
                              clStandard: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(mixed?.oneStation?.totals?.clStandard)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={oneStationMultipliers.clAdditional}
                          onChange={(e) =>
                            setOneStationMultipliers({
                              ...oneStationMultipliers,
                              clAdditional: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(mixed?.oneStation?.totals?.clAdditional)
                          }
                        />
                        {/* <span className="star-icon">★</span> */}
                      </td>
                    </tr>
                    <tr className="subtotal-row">
                      <td style={{ fontWeight: "bold" }}>Sub Total</td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            mixed?.oneStation?.totals?.mkStandard,
                            oneStationMultipliers.mkStandard
                          )
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            mixed?.oneStation?.totals?.mkAdditional,
                            oneStationMultipliers.mkAdditional
                          )
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            mixed?.oneStation?.totals?.clStandard,
                            oneStationMultipliers.clStandard
                          )
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            mixed?.oneStation?.totals?.clAdditional,
                            oneStationMultipliers.clAdditional
                          )
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* Clear Section */}
            {clear?.oneStation && (
              <>
                <div
                  style={{
                    marginTop: "2rem",
                    marginBottom: "1rem",
                    backgroundColor: "#B33791",
                    color: "white",
                    padding: "12px 16px",
                    borderRadius: "4px",
                    fontWeight: "600",
                    fontSize: "18px",
                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  Clear Units - CL Only
                </div>

                {/* One Station Clear Table */}
                <table className="payroll-table">
                  <thead>
                    <tr>
                      <th
                        style={{ backgroundColor: "#B33791", color: "white" }}
                      >
                        One Station Code's
                      </th>
                      <th
                        style={{ backgroundColor: "#B33791", color: "white" }}
                      >
                        MK Standard
                      </th>
                      <th
                        style={{ backgroundColor: "#B33791", color: "white" }}
                      >
                        MK Standard Additional
                      </th>
                      <th
                        style={{ backgroundColor: "#B33791", color: "white" }}
                      >
                        CL Standard
                      </th>
                      <th
                        style={{ backgroundColor: "#B33791", color: "white" }}
                      >
                        CL Standard Additional
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {clear?.oneStation?.rows?.map((row, index) => (
                      <tr key={index}>
                        <td>{row?.station}</td>
                        <td>{row?.mkStandard}</td>
                        <td>{row?.mkAdditional}</td>
                        <td>{row?.clStandard}</td>
                        <td>{row?.clAdditional}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td style={{ fontWeight: "bold" }}>Total</td>
                      <td>{clear?.oneStation?.totals?.mkStandard}</td>
                      <td>{clear?.oneStation?.totals?.mkAdditional}</td>
                      <td>{clear?.oneStation?.totals?.clStandard}</td>
                      <td>{clear?.oneStation?.totals?.clAdditional}</td>
                    </tr>
                    <tr className="multiplier-row">
                      <td>Multiplier</td>
                      <td>
                        <input
                          type="number"
                          value={oneStationClearMultipliers.mkStandard}
                          onChange={(e) =>
                            setOneStationClearMultipliers({
                              ...oneStationClearMultipliers,
                              mkStandard: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(clear?.oneStation?.totals?.mkStandard)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={oneStationClearMultipliers.mkAdditional}
                          onChange={(e) =>
                            setOneStationClearMultipliers({
                              ...oneStationClearMultipliers,
                              mkAdditional: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(clear?.oneStation?.totals?.mkAdditional)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={oneStationClearMultipliers.clStandard}
                          onChange={(e) =>
                            setOneStationClearMultipliers({
                              ...oneStationClearMultipliers,
                              clStandard: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(clear?.oneStation?.totals?.clStandard)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          value={oneStationClearMultipliers.clAdditional}
                          onChange={(e) =>
                            setOneStationClearMultipliers({
                              ...oneStationClearMultipliers,
                              clAdditional: e.target.value,
                            })
                          }
                          disabled={
                            !hasValue(clear?.oneStation?.totals?.clAdditional)
                          }
                        />
                      </td>
                    </tr>
                    <tr className="subtotal-row">
                      <td style={{ fontWeight: "bold" }}>Sub Total</td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            clear?.oneStation?.totals?.mkStandard,
                            oneStationClearMultipliers.mkStandard
                          )
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            clear?.oneStation?.totals?.mkAdditional,
                            oneStationClearMultipliers.mkAdditional
                          )
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            clear?.oneStation?.totals?.clStandard,
                            oneStationClearMultipliers.clStandard
                          )
                        )}
                      </td>
                      <td>
                        {formatCurrency(
                          calculateSubtotal(
                            clear?.oneStation?.totals?.clAdditional,
                            oneStationClearMultipliers.clAdditional
                          )
                        )}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            )}

            {/* Grand Total Tables */}
            {showGrandTotal && mixedGrandTotal && (
              <>
                {/* Mixed Grand Total */}
                {mixed?.oneStation && (
                  <div style={{ marginTop: "3rem" }}>
                    <div
                      style={{
                        marginBottom: "1rem",
                        backgroundColor: "#0068af",
                        color: "white",
                        padding: "12px 16px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "18px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    >
                      Mixed Grand Total
                    </div>
                    <table className="payroll-table">
                      <thead>
                        <tr>
                          <th></th>
                          <th>MK Standard</th>
                          <th>MK Standard Additional</th>
                          <th>CL Standard</th>
                          <th>CL Standard Additional</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: "bold" }}>Grand Total</td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(mixedGrandTotal.mkStandard)}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(mixedGrandTotal.mkAdditional)}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(mixedGrandTotal.clStandard)}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(mixedGrandTotal.clAdditional)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Clear Grand Total */}
                {clearGrandTotal && clear?.oneStation && (
                  <div style={{ marginTop: "2rem" }}>
                    <div
                      style={{
                        marginBottom: "1rem",
                        backgroundColor: "#B33791",
                        color: "white",
                        padding: "12px 16px",
                        borderRadius: "4px",
                        fontWeight: "600",
                        fontSize: "18px",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      }}
                    >
                      Clear Grand Total
                    </div>
                    <table className="payroll-table">
                      <thead>
                        <tr>
                          <th
                            style={{
                              backgroundColor: "#B33791",
                              color: "white",
                            }}
                          ></th>
                          <th
                            style={{
                              backgroundColor: "#B33791",
                              color: "white",
                            }}
                          >
                            MK Standard
                          </th>
                          <th
                            style={{
                              backgroundColor: "#B33791",
                              color: "white",
                            }}
                          >
                            MK Standard Additional
                          </th>
                          <th
                            style={{
                              backgroundColor: "#B33791",
                              color: "white",
                            }}
                          >
                            CL Standard
                          </th>
                          <th
                            style={{
                              backgroundColor: "#B33791",
                              color: "white",
                            }}
                          >
                            CL Standard Additional
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td style={{ fontWeight: "bold" }}>Grand Total</td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(clearGrandTotal.mkStandard)}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(clearGrandTotal.mkAdditional)}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(clearGrandTotal.clStandard)}
                          </td>
                          <td style={{ fontWeight: "bold" }}>
                            {formatCurrency(clearGrandTotal.clAdditional)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        {!showGrandTotal || hasMultipliersChanged() ? (
          <Button onClick={handleGenerateGrandTotal} variant="primary">
            Generate Grand Total
          </Button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <FaCheck style={{ color: "green", fontSize: "18px" }} />
            <span style={{ color: "green", fontWeight: "500" }}>
              Grand Total Generated
            </span>
          </div>
        )}
        {showGrandTotal && !hasMultipliersChanged() && (
          <Button onClick={handleExportToExcel} variant="primary">
            Export
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default HaltonHillsPerTicketReport;
